import React, { createContext, useContext, useState } from 'react'
import {
  includes,
  find,
  filter,
  forEach,
  sortBy,
  get
} from 'lodash'
import {useApplicationState} from "../state";
import AccountManager from "../managers/account";
//import { useApplicationState, AccountManager } from '../root'

const Preferences = createContext()

export const PreferencesProvider = ({ children }) => {
  const preferencesState = createPreferencesState()

  return (
    <Preferences.Provider value={preferencesState}>
      {children}
    </Preferences.Provider>
  )
}

export const usePreferences = () => (
  useContext(Preferences)
)

const sportsFieldName = 'sports'
const categoryFieldName = 'categories'
const tournamentsFieldName = 'tournaments'
const eventsFieldName = 'events'
const marketTypeFieldName = 'marketTypes'

export const createPreferencesState = () => {
  const {
    preferences
  } = useApplicationState()

  const [isLoading, setIsLoading] = useState(false)

  const favoriteSports = get(preferences, sportsFieldName, [])
  const favoriteCategories = get(preferences, categoryFieldName, [])
  const favoriteTournaments = get(preferences, tournamentsFieldName, [])
  const favoriteEvents = get(preferences, eventsFieldName, [])
  const favoriteMarketTypes = get(preferences, marketTypeFieldName, [])

  const setPreferences = async (newPreferences) => {
    if (isLoading) return
    setIsLoading(true)
    await AccountManager.setClientPreferences(newPreferences)
    setIsLoading(false)
  }

  const getIsSportFavorite = ({ sportId }) => {
    const sportIdNumber = Number(sportId)
    return includes(favoriteSports, sportIdNumber)
  }

  const getIsCategoryFavorite = ({ sportId, categoryId }) => {
    const sportIdNumber = Number(sportId)
    const categoryIdNumber = Number(categoryId)
    return find(favoriteCategories, { sportId: sportIdNumber, categoryId: categoryIdNumber })
  }

  const getIsTournamentFavorite = ({ tournamentId }) => {
    const tournamentIdNumber = Number(tournamentId)
    return includes(favoriteTournaments, tournamentIdNumber)
  }

  const getIsEventFavorite = ({ eventId }) => {
    const eventIdNumber = Number(eventId)
    return includes(favoriteEvents, eventIdNumber)
  }

  const getIsMarketTypeFavorite = ({ marketTypeId }) => {
    const marketTypeIdNumber = Number(marketTypeId)
    return includes(favoriteMarketTypes, marketTypeIdNumber)
  }

  const selectFavoriteSport = ({ sportId }) => {
    const sportIdNumber = Number(sportId)
    const isSelected = getIsSportFavorite({ sportId })

    return setPreferences(
      isSelected
        ? {
          ...preferences,
          [sportsFieldName]: filter(favoriteSports, id => id !== sportIdNumber)
        }
        : {
          ...preferences,
          [sportsFieldName]: [
            ...favoriteSports,
            sportIdNumber
          ]
        }
    )
  }

  const selectFavoriteCategory = ({ sportId, categoryId }) => {
    const sportIdNumber = Number(sportId)
    const categoryIdNumber = Number(categoryId)
    const isSelected = getIsCategoryFavorite({ sportId, categoryId })

    return setPreferences(
      isSelected
        ? {
          ...preferences,
          [categoryFieldName]: filter(favoriteCategories, c => c.categoryId !== categoryIdNumber && c.sportId === sportIdNumber)
        }
        : {
          ...preferences,
          [categoryFieldName]: [
            ...favoriteCategories,
            { sportId: sportIdNumber, categoryId: categoryIdNumber }
          ]
        }
    )
  }

  const selectFavoriteTournament = ({ tournamentId }) => {
    const tournamentIdNumber = Number(tournamentId)
    const isSelected = getIsTournamentFavorite({ tournamentId })

    return setPreferences(
      isSelected
        ? {
          ...preferences,
          [tournamentsFieldName]: filter(favoriteTournaments, id => id !== tournamentIdNumber)
        }
        : {
          ...preferences,
          [tournamentsFieldName]: [
            ...favoriteTournaments,
            tournamentIdNumber
          ]
        }
    )
  }

  const setFavoriteEvents = (favoriteEvents) => {
    return setPreferences({
      ...preferences,
      [eventsFieldName]: favoriteEvents
    })
  }

  const selectFavoriteEvent = ({ eventId }) => {
    const eventIdNumber = Number(eventId)
    const isSelected = getIsEventFavorite({ eventId })

    return setPreferences(
      isSelected
        ? {
          ...preferences,
          [eventsFieldName]: filter(favoriteEvents, id => id !== eventIdNumber)
        }
        : {
          ...preferences,
          [eventsFieldName]: [
            ...favoriteEvents,
            eventIdNumber
          ]
        }
    )
  }

  const selectFavoriteMarketType = ({ marketTypeId }) => {
    const marketTypeIdNumber = Number(marketTypeId)
    const isSelected = includes(favoriteMarketTypes, marketTypeIdNumber)

    return setPreferences(
      isSelected
        ? {
          ...preferences,
          [marketTypeFieldName]: filter(favoriteMarketTypes, id => id !== marketTypeIdNumber)
        }
        : {
          ...preferences,
          [marketTypeFieldName]: [
            ...favoriteMarketTypes,
            marketTypeIdNumber
          ]
        }
    )
  }

  const getFavoriteSports = sports => {
    const favoriteSports = []
    const notFavoriteSports = []

    forEach(sports, (sport) => {
      const { id } = sport
      if (getIsSportFavorite({ sportId: id })) {
        favoriteSports.push(sport)
      } else {
        notFavoriteSports.push(sport)
      }
    })

    return [
      ...sortBy(favoriteSports, ['position', 'name']),
      ...sortBy(notFavoriteSports, ['position', 'name'])
    ]
  }

  const getFavoriteCategories = categories => {
    const favoriteCategories = []
    const notFavoriteCategories = []

    forEach(categories, (category) => {
      const { sportId, id } = category
      if (getIsCategoryFavorite({
        sportId,
        categoryId: id
      })) {
        favoriteCategories.push(category)
      } else {
        notFavoriteCategories.push(category)
      }
    })

    return [
      ...sortBy(favoriteCategories, ['position', 'name']),
      ...sortBy(notFavoriteCategories, ['position', 'name'])
    ]
  }

  const getFavoriteTournaments = tournaments => {
    const favoriteTournaments = []
    const regularTournaments = []

    forEach(tournaments, (tournament) => {
      const { id } = tournament
      if (getIsTournamentFavorite({ tournamentId: id })) {
        favoriteTournaments.push(tournament)
      } else {
        regularTournaments.push(tournament)
      }
    })

    return [
      ...sortBy(favoriteTournaments, ['position', 'name']),
      ...sortBy(regularTournaments, ['position', 'name'])
    ]
  }

  return {
    preferences,
    favoriteSports,
    favoriteCategories,
    favoriteTournaments,
    favoriteEvents,
    favoriteMarketTypes,
    getIsSportFavorite,
    getIsCategoryFavorite,
    getIsTournamentFavorite,
    getIsEventFavorite,
    getIsMarketTypeFavorite,
    selectFavoriteSport,
    selectFavoriteCategory,
    selectFavoriteTournament,
    setFavoriteEvents,
    selectFavoriteEvent,
    selectFavoriteMarketType,
    getFavoriteSports,
    getFavoriteCategories,
    getFavoriteTournaments
  }
}
